import { BaseEntity, AuditedEntity, PagedEntity, Comment } from "app/shared/model/app.model";
import { FileAttachment } from "./file-attachments/file-attachment.model";
import { Self, HREFTemplated, SelfCreatedUpdatedBy } from "app/shared/model/link.model";
import { NameDescSortId, HasId } from "app/shared/model/properties.model";
import { HasLogRequest, BaseProjections, NoteProjections } from "app/shared/model/request-parameter.model";
import { TagValue } from "ng/src/app/tags/tag.model";
import { NoteTemplateValue } from "ng/src/app/templates/shared/notetemplatevalue.model";
import { CreatedUpdatedBy, LogBookUser, LogUser } from "ng/src/app/users/shared/user.model";
import { TemplateSegment } from "ng/src/app/templates/shared/template.model";
import { Log } from "ng/src/app/logs/log.model";

export interface NoteField extends NameDescSortId {
	_links: Self & {
		noteField: HREFTemplated
	};
}

export module Note {

	export interface Query extends HasLogRequest {
		acknowledgementPendingFor?: number;
		beforeNoteId?: number;
		createdBy?: number;
		diagnostic?: boolean;
		excludeIds?: number | number[];
		fromDate?: string;
		fromTimeframe?: string;
		hasAttachments?: boolean;
		hoursAgo?: number;
		includeComments?: boolean;
		isMostRecent?: boolean;
		note?: string;
		noteQuery?: string;
		projection?: string | BaseProjections | NoteProjections;
		report?: number;
		tagValues?: number;
		template?: number | number[];
		templateName?: string;
		templateValueQuery?: string;
		thruDate?: string;
		thruTimeframe?: string;
		turnover?: boolean;
		updated?: string;
		updatedBy?: number;
		updatedFromDate?: string;
		signoffPendingFor?: number;
		sameKeyFieldsAsNoteId?: number;
		beforeNoteIdByNoteTime?: number;
		mustAckForShiftSignIn?: number;
	}

	export interface Model extends RequiredProperties, Partial<OptionalProperties> {}

	export module Projections {

		export interface Onsite extends BaseEntity {
			date: string;
			tagValues: TagValue[];
			noteComments: NoteComment.Projections.Onsite[];
			templateValues: NoteTemplateValue.Projections.Onsite[];
			templateId: number;
			templateName: string;
			logId: number;
		}

		export interface Sentinel extends BaseEntity {
			noteTime: string;
			tagValues: TagValue[];
			noteComments: Array<Pick<NoteComment, 'id' | 'comment' | 'updated' | 'created' | 'createdBy' & {
				updateBy: CreatedUpdatedBy;
			}>>;
			templateValues: NoteTemplateValue[];
			templateId?: number;
			templateName?: string;
			logId: number;
			draft: boolean;
		}

		export interface TemplateValues extends Note {
			templateValues: NoteTemplateValue[];
		}

		export interface FastTemplateValues extends Note {
			templateValues: Array<NoteTemplateValue.FastTemplateValue>
		}
		
		export interface KeyNoteTemplateValues extends Note {
			templateValues: NoteTemplateValue[];
			logName: string;
		}
	}

	interface RequiredProperties {
		log: string;
		note: string;
		noteTime: string;
	}

	interface OptionalProperties {
		carryForwardEndTime: string;
		turnover: boolean;
		draft: boolean;
		diagnostic: boolean;
		newAttachments: number[];
		removeAttachments: number[];
		tagValues: string[];
	}
	
	export function getNoteName(note: Pick<Note, 'templateName' | 'keyIndicator' | 'subTemplateName'>) {
		const baseName = note.subTemplateName ?? note.templateName;
		if (baseName == null) return null;
		return baseName + (note.keyIndicator ? ` - ${note.keyIndicator}` : '');
	}
}

export interface Note extends BaseEntity {
	acknowledgements: Array<NoteAcknowledgement>;
	keyIndicator: string;
	attachments: FileAttachment[];
	carryForward: boolean;
	carryForwardEndTime?: string;
	coCreators: LogUser[];
	collaborators: LogBookUser[];
	commentsToShow: number;
	draft: boolean;
	hasSignoffData: boolean;
	logId: number;
	maxHeight: number;
	maxPriority: number;
	newMillis: number;
	note?: string;
	noteComments: NoteComment[];
	noteTime: string;
	numRevisions: number;
	richText?: string;
	safeHtmlNote: string;
	subTemplateId?: number;
	subTemplateName?: string;
	tabularFormat: boolean;
	tagValues: TagValue[];
	templateId?: number;
	templateName?: string;
	timeframe: string;
	turnover: boolean;
	updatedOrTimeframeEnded: string;
	_links: SelfCreatedUpdatedBy & {
		acknowledgements: HREFTemplated;
		attachments: HREFTemplated;
		coCreators: HREFTemplated;
		existingMetadata: HREFTemplated;
		log: HREFTemplated;
		note: HREFTemplated;
		noteComments: HREFTemplated;
		subTemplate: HREFTemplated;
		tagValues: HREFTemplated;
		template: HREFTemplated;
	}
}

export interface NoteEntity extends Pick<Note, 'created' | 'updated' | 'id' | 'noteTime' | 'carryForwardEndTime' | 'note' | 'richText' | 'turnover' | 'subTemplateId'
	| 'maxPriority' | 'timeframe' | 'updatedOrTimeframeEnded' | 'numRevisions' | 'draft' | 'commentsToShow' | 'newMillis' | 'maxHeight' | 'carryForward'>{
	evaluateAlertsTime?: string;
	diagnostic: boolean;
	_embedded: CreatedUpdatedBy & {
		coCreators: LogUser[],
		subTemplate?: TemplateSegment,
		log: Log,
		acknowledgements: NoteAcknowledgement[],
	}
}

export module NoteComment {
	export module Projections {
		export interface Onsite extends NoteComment {
			templateValues: NoteTemplateValue.Projections.Onsite[];
		}
	}
}

export interface NoteComment extends Comment {
	noteId: number;
	_links: SelfCreatedUpdatedBy & {
		note: HREFTemplated;
	}
}

export type NoteAcknowledgement = AuditedEntity & HasId & {
	noteId: number;
	_links: SelfCreatedUpdatedBy & {
		note: HREFTemplated
	};
}

export type NoteRenderUpdateResult = { found: number, attempted: { updated: number[], failed: Record<number, string> }};

export interface NoteResponse<T extends Note> extends PagedEntity<T>{}

export type NoteProjectionType<T extends NoteProjections> = (
	T extends NoteProjections.fastTemplateValues ? Note.Projections.FastTemplateValues :
	T extends NoteProjections.templateValues ? Note.Projections.TemplateValues :
	T extends NoteProjections.keyTemplateValues ? Note.Projections.KeyNoteTemplateValues :
	T extends NoteProjections.onsite ? Note.Projections.Onsite :
	T extends NoteProjections.sentinel ? Note.Projections.Sentinel :
	T extends NoteProjections.BASE ? Note :
	NoteEntity
)